import { customerMarketShopfrontApi } from '@envato/foundation-types'
import { ThunkAction } from 'redux-thunk'

import { noIndex, setCanonicalPath, setSeoMeta } from '../actions/common'
import { getBookmarksForItems } from '../actions/user/bookmark'
import { reportPageType } from '../analytics'
import { reportProductsImpressions } from '../analytics/resultAnalytics'
import { IRootState } from '../reducers'
import axiosVerbose from '../utils/axios/axiosVerbose'
import { incrementViewsForSurvey } from '../utils/tracking/trackPageViewForSurvey'
import { logError } from '../utils/errors'
import { PageFetchAction } from '../../typings/pageComponent'

export enum TypeKeys {
  GET_TOP_SELLERS_PAGE_API_REQUEST = 'GET_TOP_SELLERS_PAGE_API_REQUEST',
  GET_TOP_SELLERS_PAGE_API_SUCCESS = 'GET_TOP_SELLERS_PAGE_API_SUCCESS',
  GET_TOP_SELLERS_PAGE_API_FAIL = 'GET_TOP_SELLERS_PAGE_API_FAIL'
}

export interface IGetTopSellersPageApiRequestAction {
  type: TypeKeys.GET_TOP_SELLERS_PAGE_API_REQUEST
}

export interface IGetTopSellersPageApiSuccessAction {
  type: TypeKeys.GET_TOP_SELLERS_PAGE_API_SUCCESS
  payload: Readonly<customerMarketShopfrontApi.topSellers.IResult>
}

export interface IGetTopSellersPageApiFailAction {
  type: TypeKeys.GET_TOP_SELLERS_PAGE_API_FAIL
}

export type ActionTypes =
  | IGetTopSellersPageApiRequestAction
  | IGetTopSellersPageApiSuccessAction
  | IGetTopSellersPageApiFailAction

export const onBeforeTopSellersPageView = (): ThunkAction<
  void,
  IRootState,
  void
> => (dispatch, getState) => {
  const { topSellers } = getState().topSellersPage
  const currentSite = getState().common.currentSite
  const experiments = getState().experiments
  const location = getState().router.location

  reportPageType('other')

  incrementViewsForSurvey(currentSite, location, experiments)

  if (topSellers) {
    reportProductsImpressions({
      items: topSellers.matches,
      currentSite,
      itemRankOffset: 1,
      page: 'Top Sellers',
      section: 'Top Selling Items'
    })
  }
}

export const fetchTopSellersPageDataBySite: PageFetchAction<ThunkAction<
  Promise<ActionTypes>,
  IRootState,
  void
>> = ({ location, apiAppUrlPrefix }) => {
  return (dispatch, getState) => {
    const { currentSite } = getState().common

    const requestAction: IGetTopSellersPageApiRequestAction = {
      type: TypeKeys.GET_TOP_SELLERS_PAGE_API_REQUEST
    }
    const { period = 'week', category } = location.query

    dispatch(requestAction)

    return axiosVerbose
      .get<customerMarketShopfrontApi.topSellers.IResult>(
        `${apiAppUrlPrefix}/shopfront-api/top_sellers/${currentSite}`,
        {
          params: {
            period,
            category
          }
        }
      )
      .then(response => {
        const action: IGetTopSellersPageApiSuccessAction = {
          payload: response.data,
          type: TypeKeys.GET_TOP_SELLERS_PAGE_API_SUCCESS
        }
        if (response.data.top_sellers) {
          const itemIds = response.data.top_sellers.matches.map(item => item.id)
          if (itemIds.length) {
            dispatch(getBookmarksForItems(itemIds))
          } else {
            dispatch(noIndex())
          }
        }
        if (response.data.metadata) {
          dispatch(setSeoMeta(response.data.metadata))
        }
        if (category) {
          dispatch(
            setCanonicalPath(`${location.pathname}?category=${category}`)
          )
        }
        return dispatch(action)
      })
      .catch(err => Promise.reject(logError(err)))
      .catch(() => {
        const action: IGetTopSellersPageApiFailAction = {
          type: TypeKeys.GET_TOP_SELLERS_PAGE_API_FAIL
        }
        return dispatch(action)
      })
  }
}
